import { useEffect, useRef, useState } from "react";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2, UserPlus, Search, Users, ChevronDown, Shield, Loader2, ImagePlus, CarTaxiFront, Pencil, MoreVertical, Eye, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function UsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("drivers");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const emptyForm = {
    email: "",
    password: "",
    name: "",
    role: "user",
    phone: "",
    department: "",
    dni: "",
    birth_date: "",
    address: "",
    city: "",
    postal_code: "",
    license_number: "",
    license_expiry: "",
    vtc_license: "",
    insurance_number: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_plate: "",
    vehicle_color: "",
    vehicle_year: "",
    itv_expiry: "",
    fomento_verified: false,
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const photoInputs = useRef({});
  const carPhotoInputs = useRef({});
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErr, setEditErr] = useState("");

  const DEPARTMENTS = [
    "Dirección / Gerencia",
    "Operaciones / Tráfico",
    "Atención al cliente",
    "Departamento de reclamaciones",
    "Recursos Humanos (RRHH)",
    "Finanzas / Contabilidad",
    "Seguridad y cumplimiento",
    "Tecnología / IT",
    "Mantenimiento / Taller",
    "Logística",
    "Monitorización y fraude",
    "Marketing",
  ];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.role !== "driver" && !form.department) {
      setErr("Selecciona un departamento para usuarios y administradores.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setForm(emptyForm);
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    await api.delete(`/users/${id}`);
    await load();
  };

  const uploadPhoto = async (userId, files) => {
    if (!files || !files[0]) return;
    const fd = new FormData();
    fd.append("file", files[0]);
    try {
      await api.post(`/users/${userId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto actualizada");
      await load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    }
  };

  const uploadCarPhoto = async (userId, files) => {
    if (!files || !files[0]) return;
    const fd = new FormData();
    fd.append("file", files[0]);
    try {
      await api.post(`/users/${userId}/car-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto del coche actualizada");
      await load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    }
  };

  const DRIVER_FIELDS = ["dni","birth_date","address","city","postal_code","license_number","license_expiry","vtc_license","insurance_number","vehicle_brand","vehicle_model","vehicle_plate","vehicle_color","vehicle_year","itv_expiry","notes"];

  const openEdit = (u) => {
    setEditUser(u);
    const base = {
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      department: u.department || "",
      password: "",
      fomento_verified: !!u.fomento_verified,
    };
    DRIVER_FIELDS.forEach((f) => (base[f] = u[f] || ""));
    setEditForm(base);
    setEditErr("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditErr("");
    setSavingEdit(true);
    try {
      const body = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        department: editUser.role === "driver" ? undefined : editForm.department,
      };
      if (editForm.password) body.password = editForm.password;
      if (editUser.role === "driver") {
        DRIVER_FIELDS.forEach((f) => (body[f] = editForm[f] ?? ""));
        body.fomento_verified = !!editForm.fomento_verified;
      }
      Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);
      await api.patch(`/users/${editUser.id}`, body);
      toast.success("Usuario actualizado");
      setEditUser(null);
      await load();
    } catch (e2) {
      setEditErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const term = search.trim().toLowerCase();
  const matches = (u) =>
    !term || [u.name, u.email, u.phone, u.file_code].some((v) => (v || "").toLowerCase().includes(term));
  const allDrivers = users.filter((u) => u.role === "driver");
  const allStaff = users.filter((u) => u.role !== "driver");
  const drivers = allDrivers.filter(matches);
  const staff = allStaff.filter(matches);

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="users-page">
      <header className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
          Administración
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Usuarios y conductores
        </h1>
      </header>

      <section className="border border-black bg-white mb-8">
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          data-testid="toggle-create-user"
          className={`w-full p-4 font-display font-semibold text-sm uppercase tracking-widest flex items-center gap-2 hover:bg-zinc-50 transition-colors ${showCreate ? "border-b border-black" : ""}`}
        >
          <UserPlus size={16} /> Crear usuario o conductor
          <ChevronDown size={16} className={`ml-auto transition-transform ${showCreate ? "rotate-180" : ""}`} />
        </button>
        {showCreate && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 p-6" data-testid="create-user-form">
          <div>
            <Label className="text-xs uppercase tracking-widest">Nombre</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="mt-2 h-11 rounded-none border-black"
              data-testid="user-name-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="mt-2 h-11 rounded-none border-black"
              data-testid="user-email-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Contraseña</Label>
            <Input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              className="mt-2 h-11 rounded-none border-black"
              data-testid="user-password-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Rol</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-2 h-11 rounded-none border-black" data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="user">Usuario / Personal</SelectItem>
                <SelectItem value="driver">Conductor</SelectItem>
                <SelectItem value="admin">Administrador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role !== "driver" && (
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest">Departamento *</Label>
              <Select
                value={form.department}
                onValueChange={(v) => setForm({ ...form, department: v })}
              >
                <SelectTrigger
                  className="mt-2 h-11 rounded-none border-black"
                  data-testid="user-department-select"
                >
                  <SelectValue placeholder="Selecciona un departamento" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Teléfono (recomendado para conductores)</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-2 h-11 rounded-none border-black"
              placeholder="+34 600 00 00 00"
            />
          </div>

          {form.role === "driver" && (
            <div className="md:col-span-2 border-2 border-black bg-zinc-50 p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="driver-extra-fields">
              <div className="md:col-span-2 font-display text-sm uppercase tracking-widest font-bold">
                Ficha del conductor
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">DNI / NIE</Label>
                <Input value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} className="mt-2 h-11 rounded-none border-black" data-testid="driver-dni" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Fecha nacimiento</Label>
                <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Dirección</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Ciudad</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Código postal</Label>
                <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>

              <div className="md:col-span-2 border-t border-black pt-3 mt-2 font-display text-xs uppercase tracking-widest text-zinc-600">
                Licencias y seguro
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Permiso conducir</Label>
                <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Caducidad permiso</Label>
                <Input type="date" value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Licencia VTC / BTP</Label>
                <Input value={form.vtc_license} onChange={(e) => setForm({ ...form, vtc_license: e.target.value })} className="mt-2 h-11 rounded-none border-black" data-testid="driver-vtc" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Póliza seguro</Label>
                <Input value={form.insurance_number} onChange={(e) => setForm({ ...form, insurance_number: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>

              <div className="md:col-span-2 border-t border-black pt-3 mt-2 font-display text-xs uppercase tracking-widest text-zinc-600">
                Vehículo
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Marca</Label>
                <Input value={form.vehicle_brand} onChange={(e) => setForm({ ...form, vehicle_brand: e.target.value })} placeholder="Mercedes, BMW…" className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Modelo</Label>
                <Input value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} placeholder="Vito, Clase E…" className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Matrícula *</Label>
                <Input value={form.vehicle_plate} onChange={(e) => setForm({ ...form, vehicle_plate: e.target.value.toUpperCase() })} placeholder="1234 ABC" className="mt-2 h-11 rounded-none border-black" data-testid="driver-plate" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Color</Label>
                <Input value={form.vehicle_color} onChange={(e) => setForm({ ...form, vehicle_color: e.target.value })} placeholder="Negro" className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Año</Label>
                <Input value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} placeholder="2022" className="mt-2 h-11 rounded-none border-black" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Caducidad ITV</Label>
                <Input type="date" value={form.itv_expiry} onChange={(e) => setForm({ ...form, itv_expiry: e.target.value })} className="mt-2 h-11 rounded-none border-black" />
              </div>

              <label className="md:col-span-2 border-2 border-black bg-white p-3 flex items-start gap-3 cursor-pointer hover:bg-zinc-100 transition-colors" data-testid="fomento-check-label">
                <input
                  type="checkbox"
                  checked={!!form.fomento_verified}
                  onChange={(e) => setForm({ ...form, fomento_verified: e.target.checked })}
                  className="mt-1 w-5 h-5 accent-black"
                  data-testid="driver-fomento-checkbox"
                />
                <div>
                  <div className="font-semibold text-sm">He verificado a este conductor en el Ministerio de Fomento</div>
                  <div className="text-xs text-zinc-600">
                    Confirma que has comprobado su licencia VTC/BTP y estado en el registro oficial de Fomento.
                  </div>
                </div>
              </label>

              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest">Notas</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-none border-black min-h-[80px]" placeholder="Observaciones internas" />
              </div>
            </div>
          )}

          {err && (
            <div className="md:col-span-2 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2">
              {err}
            </div>
          )}

          <div className="md:col-span-2 flex justify-end">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
              data-testid="create-user-btn"
            >
              {submitting ? <Loader2 className="animate-spin" size={14} /> : "Crear"}
            </Button>
          </div>
        </form>
        )}
      </section>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex border border-black" data-testid="users-tabs">
          {[
            ["drivers", `Conductores (${allDrivers.length})`, CarTaxiFront],
            ["staff", `Equipo (${allStaff.length})`, Users],
          ].map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`users-tab-${k}`}
              style={tab === k ? { color: "#fff" } : undefined}
              className={`flex-1 sm:flex-none px-4 h-11 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-widest transition-colors ${tab === k ? "bg-black" : "bg-white hover:bg-zinc-100"}`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === "drivers" ? "Buscar conductor por nombre, email, teléfono o expediente…" : "Buscar en el equipo…"}
            className="rounded-none border-black pl-9 h-11"
            data-testid="users-search"
          />
        </div>
      </div>

      {tab === "drivers" && (
      <section className="border border-black bg-white mb-8">
        <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest flex items-center gap-2">
          <CarTaxiFront size={16} /> Conductores ({drivers.length})
        </div>
        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="drivers-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}></th>
                <th>Nombre</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-sm text-zinc-500 py-6 text-center">Cargando…</td></tr>
              ) : drivers.length === 0 ? (
                <tr><td colSpan={5} className="text-sm text-zinc-500 py-6 text-center">{term ? "Ningún conductor coincide con la búsqueda." : "Aún no hay conductores."}</td></tr>
              ) : drivers.map((d) => (
                <tr key={d.id} data-testid={`driver-row-${d.email}`}>
                  <td>
                    <div className="w-10 h-10 border border-black bg-zinc-100 overflow-hidden flex items-center justify-center">
                      {d.photo_path ? (
                        <img
                          src={`${API}/users/${d.id}/photo?auth=${encodeURIComponent(getToken())}&t=${d.photo_path?.length || 0}`}
                          alt={d.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-bold text-zinc-400">
                          {d.name?.slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="font-semibold">
                    {d.name}
                    <div className="font-mono text-[11px] text-zinc-500" data-testid={`driver-file-code-${d.email}`}>Exp. {d.file_code || "—"}</div>
                  </td>
                  <td className="text-sm">{d.email}</td>
                  <td className="text-sm">{d.phone || "—"}</td>
                  <td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-2 hover:bg-zinc-100 inline-flex items-center justify-center"
                          data-testid={`driver-menu-${d.email}`}
                          aria-label="Acciones"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-none border-black w-56">
                        <DropdownMenuItem
                          onClick={() => navigate(`/drivers/${d.id}`)}
                          className="rounded-none cursor-pointer"
                          data-testid={`driver-view-${d.email}`}
                        >
                          <Eye size={14} className="mr-2" /> Visualizar datos
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate(`/drivers/${d.id}/amonestaciones`)}
                          className="rounded-none cursor-pointer"
                          data-testid={`driver-sanctions-${d.email}`}
                        >
                          <AlertTriangle size={14} className="mr-2" /> Informes de amonestación
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => photoInputs.current[d.id]?.click()}
                          className="rounded-none cursor-pointer"
                        >
                          <ImagePlus size={14} className="mr-2" /> {d.photo_path ? "Cambiar foto" : "Subir foto"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => carPhotoInputs.current[d.id]?.click()}
                          className="rounded-none cursor-pointer"
                          data-testid={`upload-car-photo-${d.email}`}
                        >
                          <ImagePlus size={14} className="mr-2" /> {d.car_photo_path ? "Cambiar foto del coche" : "Subir foto del coche"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openEdit(d)}
                          className="rounded-none cursor-pointer"
                          data-testid={`driver-edit-${d.email}`}
                        >
                          <Pencil size={14} className="mr-2" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => remove(d.id)}
                          className="rounded-none cursor-pointer text-[#E63946] focus:text-[#E63946]"
                          data-testid={`driver-delete-${d.email}`}
                          disabled={d.id === user.id}
                        >
                          <Trash2 size={14} className="mr-2" /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <input
                      ref={(el) => (photoInputs.current[d.id] = el)}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => uploadPhoto(d.id, e.target.files)}
                    />
                    <input
                      ref={(el) => (carPhotoInputs.current[d.id] = el)}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => uploadCarPhoto(d.id, e.target.files)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {tab === "staff" && (
      <section className="border border-black bg-white">
        <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
          Equipo ({staff.length})
        </div>
        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="users-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Departamento</th>
                <th>Teléfono</th>
                <th>Rol</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {staff.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="font-semibold">
                    {u.name}
                    <div className="font-mono text-[11px] text-zinc-500" data-testid={`user-file-code-${u.email}`}>Exp. {u.file_code || "—"}</div>
                  </td>
                  <td>{u.email}</td>
                  <td>{u.department || <span className="text-zinc-400">—</span>}</td>
                  <td>{u.phone || "—"}</td>
                  <td>
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest">
                      {u.role === "admin" && <Shield size={12} />}
                      {u.role}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-3 justify-end">
                      <button
                        onClick={() => openEdit(u)}
                        className="hover:underline text-xs uppercase tracking-widest inline-flex items-center gap-1"
                        data-testid={`edit-user-${u.email}`}
                      >
                        <Pencil size={12} /> Editar
                      </button>
                      {u.id !== user.id && (
                        <button
                          onClick={() => remove(u.id)}
                          className="text-[#E63946] hover:underline text-xs uppercase tracking-widest inline-flex items-center gap-1"
                        >
                          <Trash2 size={12} /> Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-none border-black max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">
              Editar {editUser?.role === "driver" ? "conductor" : "usuario"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nombre</Label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                className="mt-2 h-11 rounded-none border-black"
                data-testid="edit-user-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                required
                className="mt-2 h-11 rounded-none border-black"
                data-testid="edit-user-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Teléfono</Label>
              <Input
                value={editForm.phone || ""}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                className="mt-2 h-11 rounded-none border-black"
                placeholder="+34 600 00 00 00"
                data-testid="edit-user-phone"
              />
            </div>
            {editUser?.role !== "driver" && (
              <div>
                <Label className="text-xs uppercase tracking-widest">Departamento</Label>
                <Select
                  value={editForm.department || ""}
                  onValueChange={(v) => setEditForm({ ...editForm, department: v })}
                >
                  <SelectTrigger className="mt-2 h-11 rounded-none border-black" data-testid="edit-user-department">
                    <SelectValue placeholder="Selecciona un departamento" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {DEPARTMENTS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editUser?.role === "driver" && (
              <div className="border-2 border-black bg-zinc-50 p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="edit-driver-personal-fields">
                <div className="md:col-span-2 font-display text-sm uppercase tracking-widest font-bold">
                  Datos personales
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">DNI / NIE</Label>
                  <Input
                    value={editForm.dni || ""}
                    onChange={(e) => setEditForm({ ...editForm, dni: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-dni"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Fecha nacimiento</Label>
                  <Input
                    type="date"
                    value={editForm.birth_date || ""}
                    onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-birth-date"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest">Dirección</Label>
                  <Input
                    value={editForm.address || ""}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-address"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Ciudad</Label>
                  <Input
                    value={editForm.city || ""}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-city"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Código postal</Label>
                  <Input
                    value={editForm.postal_code || ""}
                    onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-postal-code"
                  />
                </div>

                <div className="md:col-span-2 border-t border-black pt-3 mt-2 font-display text-xs uppercase tracking-widest text-zinc-600">
                  Licencias y seguro
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Permiso conducir</Label>
                  <Input
                    value={editForm.license_number || ""}
                    onChange={(e) => setEditForm({ ...editForm, license_number: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-license-number"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Caducidad permiso</Label>
                  <Input
                    type="date"
                    value={editForm.license_expiry || ""}
                    onChange={(e) => setEditForm({ ...editForm, license_expiry: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-license-expiry"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Licencia VTC / BTP</Label>
                  <Input
                    value={editForm.vtc_license || ""}
                    onChange={(e) => setEditForm({ ...editForm, vtc_license: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-vtc-license"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Póliza seguro</Label>
                  <Input
                    value={editForm.insurance_number || ""}
                    onChange={(e) => setEditForm({ ...editForm, insurance_number: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-insurance-number"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest">Notas</Label>
                  <Textarea
                    value={editForm.notes || ""}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    className="mt-2 rounded-none border-black min-h-[80px]"
                    placeholder="Observaciones internas"
                    data-testid="edit-driver-notes"
                  />
                </div>
              </div>
            )}
            {editUser?.role === "driver" && (
              <div className="border-2 border-black bg-zinc-50 p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="edit-driver-vehicle-fields">
                <div className="md:col-span-2 font-display text-sm uppercase tracking-widest font-bold">
                  Vehículo del conductor
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Marca</Label>
                  <Input
                    value={editForm.vehicle_brand || ""}
                    onChange={(e) => setEditForm({ ...editForm, vehicle_brand: e.target.value })}
                    placeholder="Mercedes, BMW…"
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-vehicle-brand"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Modelo</Label>
                  <Input
                    value={editForm.vehicle_model || ""}
                    onChange={(e) => setEditForm({ ...editForm, vehicle_model: e.target.value })}
                    placeholder="Vito, Clase E…"
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-vehicle-model"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Matrícula</Label>
                  <Input
                    value={editForm.vehicle_plate || ""}
                    onChange={(e) => setEditForm({ ...editForm, vehicle_plate: e.target.value.toUpperCase() })}
                    placeholder="1234 ABC"
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-vehicle-plate"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Color</Label>
                  <Input
                    value={editForm.vehicle_color || ""}
                    onChange={(e) => setEditForm({ ...editForm, vehicle_color: e.target.value })}
                    placeholder="Negro"
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-vehicle-color"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Año</Label>
                  <Input
                    value={editForm.vehicle_year || ""}
                    onChange={(e) => setEditForm({ ...editForm, vehicle_year: e.target.value })}
                    placeholder="2022"
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-vehicle-year"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Caducidad ITV</Label>
                  <Input
                    type="date"
                    value={editForm.itv_expiry || ""}
                    onChange={(e) => setEditForm({ ...editForm, itv_expiry: e.target.value })}
                    className="mt-2 h-11 rounded-none border-black"
                    data-testid="edit-driver-itv-expiry"
                  />
                </div>
                <label className="md:col-span-2 border-2 border-black bg-white p-3 flex items-start gap-3 cursor-pointer hover:bg-zinc-100 transition-colors" data-testid="edit-driver-fomento-label">
                  <input
                    type="checkbox"
                    checked={!!editForm.fomento_verified}
                    onChange={(e) => setEditForm({ ...editForm, fomento_verified: e.target.checked })}
                    className="mt-1 w-5 h-5 accent-black"
                    data-testid="edit-driver-fomento-checkbox"
                  />
                  <div>
                    <div className="font-semibold text-sm">Verificado en el Ministerio de Fomento</div>
                    <div className="text-xs text-zinc-600">
                      Marca si has comprobado su licencia VTC/BTP y estado en el registro oficial.
                    </div>
                  </div>
                </label>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest">
                Nueva contraseña (opcional)
              </Label>
              <Input
                type="text"
                value={editForm.password || ""}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                minLength={6}
                className="mt-2 h-11 rounded-none border-black"
                placeholder="Dejar vacío para no cambiar"
                data-testid="edit-user-password"
              />
            </div>
            {editErr && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2">
                {editErr}
              </div>
            )}
            <DialogFooter className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditUser(null)}
                className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingEdit}
                className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
                data-testid="save-edit-user-btn"
              >
                {savingEdit ? <Loader2 className="animate-spin" size={14} /> : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
